/**
 * Timezone Utility for Frontend - All dates use America/Chicago (Central Time)
 * 
 * This ensures consistent date handling matching the backend.
 */

const TIMEZONE = 'America/Chicago';

/**
 * Get current date/time components in Chicago timezone
 * Returns individual components to avoid Date parsing issues
 * @returns {Object} { year, month, day, hour, minute, second, dayOfWeek }
 */
function getChicagoTime() {
  const now = new Date();
  
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    weekday: 'short'
  });
  
  const parts = formatter.formatToParts(now);
  const get = (type) => parts.find(p => p.type === type)?.value;
  
  const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
  
  return {
    year: parseInt(get('year')),
    month: parseInt(get('month')),
    day: parseInt(get('day')),
    hour: parseInt(get('hour')),
    minute: parseInt(get('minute')),
    second: parseInt(get('second')),
    dayOfWeek: dayMap[get('weekday')] // 0 = Sunday, 1 = Monday, etc.
  };
}

/**
 * Get today's date as YYYY-MM-DD string in Chicago timezone
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function getTodayStr() {
  const t = getChicagoTime();
  return `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
}

/**
 * Get current day of week in Chicago timezone
 * @returns {number} Day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 */
export function getCurrentDayOfWeek() {
  return getChicagoTime().dayOfWeek;
}

/**
 * Get current time in minutes since midnight in Chicago timezone
 * @returns {number} Minutes since midnight
 */
export function getCurrentTimeMinutes() {
  const t = getChicagoTime();
  return t.hour * 60 + t.minute;
}

/**
 * Calculate the next order date for a given day of week
 * MUST match backend logic exactly
 * @param {number} dayOfWeekId - 1=Monday, 2=Tuesday, ..., 7=Sunday (database format)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function calculateNextOrderDate(dayOfWeekId) {
  const t = getChicagoTime();
  const currentDay = t.dayOfWeek; // 0 = Sunday, 1 = Monday, etc.
  const targetDay = dayOfWeekId === 7 ? 0 : dayOfWeekId; // Convert Sunday from 7 to 0

  let daysUntilTarget = targetDay - currentDay;
  if (daysUntilTarget < 0) {
    daysUntilTarget += 7; // Next week (day already passed)
  }
  // If daysUntilTarget === 0, it's today - keep it as today
  // Cutoff validation happens separately

  // Use Date.UTC to avoid browser-local timezone interference
  const targetDate = new Date(Date.UTC(t.year, t.month - 1, t.day + daysUntilTarget, 12, 0, 0));
  
  const year = targetDate.getUTCFullYear();
  const month = String(targetDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(targetDate.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a cutoff time string (HH:MM:SS) to minutes since midnight
 * @param {string} cutoffTime - Time in HH:MM:SS format
 * @returns {number} Minutes since midnight
 */
export function parseCutoffTime(cutoffTime) {
  const [hours, minutes] = cutoffTime.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Check if current time has passed a cutoff time for today
 * @param {string} cutoffTime - Time in HH:MM:SS format
 * @returns {boolean} True if cutoff has passed
 */
export function hasCutoffPassed(cutoffTime) {
  const currentMinutes = getCurrentTimeMinutes();
  const cutoffMinutes = parseCutoffTime(cutoffTime);
  return currentMinutes >= cutoffMinutes;
}

/**
 * Format a date for display in Chicago timezone
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { 
    timeZone: TIMEZONE,
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
}

/**
 * Format a timestamp for display in Chicago timezone
 * @param {Date|string} timestamp - Timestamp to format
 * @returns {string} Formatted timestamp string
 */
export function formatTimestamp(timestamp) {
  const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return d.toLocaleString('en-US', { 
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format a cutoff time string (HH:MM:SS) to AM/PM format
 * @param {string} cutoffTime - Time in HH:MM:SS format (e.g., "09:00:00")
 * @returns {string} Formatted time (e.g., "9:00 AM")
 */
export function formatCutoffTime(cutoffTime) {
  const [hours, minutes] = cutoffTime.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12; // Convert 0 to 12, 13 to 1, etc.
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

export default {
  TIMEZONE,
  getChicagoTime,
  getTodayStr,
  getCurrentDayOfWeek,
  getCurrentTimeMinutes,
  calculateNextOrderDate,
  parseCutoffTime,
  formatCutoffTime,
  hasCutoffPassed,
  formatDate,
  formatTimestamp
};
